import { useState } from "react";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { Lock, Loader2, Copy, Check, Megaphone, Target, Ban, FileText, Link2, Settings } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function copyText(text, done) {
  if (navigator.clipboard?.writeText) {
    navigator.clipboard.writeText(text).then(done).catch(fallback);
  } else fallback();
  function fallback() {
    const ta = document.createElement("textarea");
    ta.value = text; ta.style.position = "fixed"; ta.style.opacity = "0";
    document.body.appendChild(ta); ta.select();
    try { document.execCommand("copy"); done(); } catch { toast.error("Impossibile copiare"); }
    document.body.removeChild(ta);
  }
}

function CopyBlock({ label, text, testid }) {
  const [c, setC] = useState(false);
  return (
    <div className="bg-neutral-950 border border-neutral-800 rounded-xl p-4" data-testid={testid}>
      <div className="flex items-center justify-between mb-2 gap-3">
        <p className="text-[10px] font-mono uppercase tracking-[0.18em] text-amber-500/80">{label}</p>
        <button onClick={() => copyText(text, () => { setC(true); setTimeout(() => setC(false), 1500); })}
          data-testid={`${testid}-copy`}
          className="flex items-center gap-1.5 text-xs font-semibold text-neutral-900 bg-amber-400 hover:bg-amber-300 rounded-full px-3 py-1.5 transition-colors shrink-0">
          {c ? <><Check size={13} /> Copiato</> : <><Copy size={13} /> Copia</>}
        </button>
      </div>
      <p className="text-sm text-neutral-200 whitespace-pre-line leading-relaxed">{text}</p>
    </div>
  );
}

function Section({ icon: Icon, title, subtitle, children }) {
  return (
    <section className="bg-neutral-900/70 border border-neutral-800 rounded-2xl p-5" data-testid={`ads-section-${title.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-center gap-2.5 mb-4">
        <Icon size={18} className="text-amber-400" />
        <div>
          <h2 className="font-bold text-lg leading-tight">{title}</h2>
          {subtitle && <p className="text-sm text-neutral-400">{subtitle}</p>}
        </div>
      </div>
      <div className="space-y-3">{children}</div>
    </section>
  );
}

function LockScreen({ onUnlock }) {
  const [pwd, setPwd] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    if (!pwd) return;
    setLoading(true); setError("");
    try {
      const { data } = await axios.post(`${API}/ig/unlock`, { password: pwd });
      localStorage.setItem("ig_token", data.token);
      onUnlock();
    } catch { setError("Password errata. Riprova."); setLoading(false); }
  };
  return (
    <div className="min-h-screen bg-neutral-950 text-neutral-100 grid place-items-center px-4" data-testid="ads-lock-screen">
      <Toaster theme="dark" position="top-right" />
      <div className="w-full max-w-sm bg-neutral-900/70 border border-neutral-800 rounded-3xl p-8 text-center">
        <div className="inline-grid place-items-center w-16 h-16 rounded-2xl mb-5 bg-amber-400">
          <Lock size={26} className="text-neutral-900" />
        </div>
        <h1 className="font-bold text-xl">Pacchetto Google Ads</h1>
        <p className="text-sm text-neutral-500 mt-1 mb-6">Area privata · inserisci la password</p>
        <form onSubmit={submit} className="space-y-3">
          <input type="password" value={pwd} autoFocus placeholder="Password"
            onChange={(e) => setPwd(e.target.value)} data-testid="ads-password-input"
            className="w-full bg-neutral-950 border border-neutral-700 focus:border-amber-400 rounded-xl py-3 px-4 text-center focus:outline-none transition-colors" />
          {error && <p className="text-sm text-rose-400" data-testid="ads-password-error">{error}</p>}
          <button type="submit" disabled={loading || !pwd} data-testid="ads-unlock-button"
            className="w-full flex items-center justify-center gap-2 rounded-xl bg-amber-400 hover:bg-amber-300 text-neutral-900 font-semibold py-3 disabled:opacity-60 transition-colors">
            {loading ? <Loader2 size={17} className="animate-spin" /> : <Lock size={16} />} Accedi
          </button>
        </form>
      </div>
    </div>
  );
}

const HEADLINES = [
  "Proteggi ciò che hai costruito",
  "Oro fisico, fuori dalle banche",
  "Calcola il tuo Piano Azione",
  "Liquidità ferma? Perde valore",
  "Diversifica con l'oro fisico",
  "Oro fisico reale, non cartaceo",
  "Protezione patrimoniale",
  "Pensato per chi ha una SRL",
  "Bene rifugio, non promesse",
  "100% riservato, nessun obbligo",
  "Un percorso, non una vendita",
  "Guida gratuita all'oro fisico",
];

const DESCRIPTIONS = [
  "Professionisti e imprenditori diversificano la liquidità ferma con oro fisico reale.",
  "Un bene tangibile, fuori dalle banche, per proteggere il patrimonio. Nessun obbligo.",
  "Calcola gratis quanto perde la tua liquidità e scarica la guida. Senza pressioni.",
  "Per famiglie, professionisti e SRL con liquidità ferma. Prenota una call riservata.",
];

export default function GoogleAds() {
  const [unlocked, setUnlocked] = useState(() => !!localStorage.getItem("ig_token"));
  if (!unlocked) return <LockScreen onUnlock={() => setUnlocked(true)} />;

  const landing = `${window.location.origin}/oro`;

  return (
    <div className="min-h-screen bg-neutral-950 text-neutral-100" data-testid="google-ads-page">
      <Toaster theme="dark" position="top-right" />
      <header className="sticky top-0 z-20 border-b border-neutral-800 bg-neutral-950/90 backdrop-blur px-4 md:px-8 py-4">
        <div className="max-w-4xl mx-auto flex items-center gap-3">
          <div className="p-2 rounded-xl bg-amber-400"><Megaphone size={20} className="text-neutral-900" /></div>
          <div>
            <h1 className="font-bold text-lg leading-none">Pacchetto Google Ads</h1>
            <p className="text-[11px] uppercase tracking-[0.2em] text-neutral-500">Acquisizione clienti · pronto da pubblicare</p>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 md:px-8 py-8 space-y-6">
        <div className="bg-amber-400/10 border border-amber-700/40 rounded-2xl p-5">
          <p className="text-sm text-amber-200/90">
            🎯 Obiettivo: intercettare imprenditori, famiglie e professionisti con liquidità ferma e portarli sulla landing <b>/oro</b> per prenotare una call riservata.
            Linguaggio filtrato per ridurre il rischio di flag "servizi finanziari". Budget di partenza contenuto, poi scala su ciò che converte.
          </p>
        </div>

        <Section icon={Settings} title="Setup campagna" subtitle="Impostazioni consigliate">
          <CopyBlock testid="ads-landing" label="URL landing (pagina di destinazione)" text={landing} />
          <CopyBlock testid="ads-utm" label="URL con tracciamento UTM (per distinguere i canali nel CRM)" text={
`Google Ads:
${landing}?utm_source=google&utm_medium=cpc

Instagram — link in bio:
${landing}?utm_source=instagram&utm_medium=bio

Instagram — DM:
${landing}?utm_source=instagram&utm_medium=dm

Instagram — Storie:
${landing}?utm_source=instagram&utm_medium=storie`} />
          <p className="text-[11px] text-neutral-500">La landing legge questi parametri e salva la provenienza sul lead (es. "Instagram · bio"), visibile nel Lead Dashboard con export CSV.</p>
          <CopyBlock testid="ads-setup" label="Configurazione consigliata" text={
`Tipo campagna: Rete di ricerca (solo Search, no Display all'inizio)
Obiettivo: Contatti (Lead)
Località: Italia (escludi eventuali regioni non target)
Lingua: Italiano
Budget iniziale: 10–20 €/giorno (test 2–3 settimane, poi scala)
Offerta: "Massimizza i clic" all'inizio → poi "Massimizza conversioni" dopo 15–20 lead
Conversione da tracciare: invio form su /oro (imposta un tag conversione sul thank-you)
Programmazione: Lun–Ven, fasce 8–22 (ottimizza dopo i primi dati)`} />
        </Section>

        <Section icon={Target} title="Ad Group 1 — Protezione patrimonio" subtitle="Top funnel · informativo">
          <CopyBlock testid="ads-kw-1" label="Keyword (una per riga)" text={
`come proteggere i risparmi dall'inflazione
dove mettere i risparmi al sicuro
alternative al conto corrente
proteggere il patrimonio aziendale
come difendersi dall'inflazione
proteggere i risparmi dalla svalutazione`} />
        </Section>

        <Section icon={Target} title="Ad Group 2 — Oro fisico" subtitle="Mid funnel · intent specifico">
          <CopyBlock testid="ads-kw-2" label="Keyword (una per riga)" text={
`oro fisico per proteggersi
comprare oro fisico
oro fisico vs oro cartaceo
oro fisico fuori dal sistema bancario
lingotti oro fisico
oro fisico come bene rifugio`} />
        </Section>

        <Section icon={Target} title="Ad Group 3 — Imprenditori & SRL" subtitle="Bottom funnel">
          <CopyBlock testid="ads-kw-3" label="Keyword (una per riga)" text={
`protezione patrimoniale imprenditori
liquidità aziendale ferma cosa fare
diversificare patrimonio SRL
proteggere liquidità SRL
tesoreria aziendale oro
diversificazione patrimoniale professionisti`} />
        </Section>

        <Section icon={Ban} title="Negative keyword" subtitle="Escludi ricerche fuori target / rischiose">
          <CopyBlock testid="ads-neg" label="Negative keyword (una per riga)" text={
`investire
investimento
rendimento
guadagnare
trading
quotazione oro oggi
prezzo oro grammo
bitcoin
azioni
gratis online
lavoro da casa
recensioni truffa`} />
        </Section>

        <Section icon={FileText} title="Annunci responsive" subtitle="Titoli e descrizioni (compliant)">
          <CopyBlock testid="ads-headlines" label={`Titoli (${HEADLINES.length}) — max 30 caratteri l'uno`} text={HEADLINES.join("\n")} />
          <CopyBlock testid="ads-descriptions" label={`Descrizioni (${DESCRIPTIONS.length}) — max 90 caratteri l'una`} text={DESCRIPTIONS.join("\n\n")} />
          <p className="text-[11px] text-neutral-500">Nota: Google mostra gli annunci "responsive" combinando titoli e descrizioni. Inserisci tutti quelli sopra; controlla che ogni titolo stia entro 30 caratteri e ogni descrizione entro 90.</p>
        </Section>

        <Section icon={Link2} title="Estensioni annuncio" subtitle="Sitelink e callout">
          <CopyBlock testid="ads-sitelinks" label="Sitelink (testo → URL, destinazioni distinte)" text={
`Piano Azione (Calcolatore) → ${window.location.origin}/calcolatore
Guida gratuita all'oro fisico → ${window.location.origin}/guida
Richiedi una call riservata → ${window.location.origin}/oro
Seguimi su Instagram → https://instagram.com/alexgold.oro`} />
          <CopyBlock testid="ads-callouts" label="Callout" text={
`Nessun obbligo
100% riservato
Bene tangibile
Fuori dal sistema bancario
Nessuna promessa di rendimento
Percorso su misura`} />
        </Section>

        <Section icon={Ban} title="Regole di linguaggio" subtitle="Applica ovunque: annunci + landing">
          <CopyBlock testid="ads-rules" label="Evita → Usa" text={
`investimento / investire      →  protezione / diversificazione
rendimento / guadagno         →  valore reale / bene rifugio
consulenza finanziaria        →  affiancamento / percorso
garantito / sicuro al 100%    →  tangibile / fuori dal sistema bancario
comprare per guadagnare       →  proteggere ciò che hai costruito`} />
          <p className="text-[11px] text-neutral-500">Tono: mai promesse di rendimento, niente urgenza artificiale. Focus su protezione patrimoniale, trasparenza e diversificazione — coerente con Alex Gold.</p>
        </Section>
      </main>
    </div>
  );
}
